/** Copyright (c) 2020 T. Rowe Price. All rights reserved. */

import { omit } from 'lodash';
import {
  INDIVIDUAL,
  JOINT_TENANT,
  COLLEGE_SAVINGS,
  ROTH_ROLLOVER_IRA,
  TRADITIONAL_IRA,
  ROTH_IRA,
  ROLLOVER_IRA,
  UGMA_UTMA,
  TRUST,
  TRANSFER_ON_DEATH
} from 'services/AccountTypes';
import {
  updateBankAssociations,
  deleteBank,
  challengeDepositVerification
} from 'services/Rest';
import Store from 'state/Store';
import { getAccountsById } from 'state/account/AccountSelectors';
import { getAccountDescriptor } from 'services/Accounts';

import { preferencesLinks } from 'services/Externals';
import { newDeleteBankEnabled } from 'services/FeatureFlags';

export const UNKNOWN = -1;
const INITIATION_FAIL = 10;
const INITIATED = 20;
const READY_FOR_VALIDATION = 30;
const VERIFIED = 40;
const FAILED = 50;
const LAPSED = 60;
const DELETED = 70;
const REINITIATED = 80;
const DEPOSIT_FAIL = 90;

export const isInitiationFail = (verification) =>
  verification === INITIATION_FAIL || verification === 'INITIATION_FAIL';
export const isInitiated = (verification) =>
  verification === INITIATED || verification === 'INITIATED';
export const isReadyForValidation = (verification) =>
  verification === READY_FOR_VALIDATION ||
  verification === 'READY_FOR_VALIDATION';
export const isVerified = (verification) =>
  verification === VERIFIED || verification === 'VERIFIED';
export const isFailed = (verification) =>
  verification === FAILED || verification === 'FAILED';
export const isLapsed = (verification) =>
  verification === LAPSED || verification === 'LAPSED';
export const isDeleted = (verification) =>
  verification === DELETED || verification === 'DELETED';
export const isReinitiated = (verification) =>
  verification === REINITIATED || verification === 'REINITIATED';
export const isDepositFail = (verification) =>
  verification === DEPOSIT_FAIL || verification === 'DEPOSIT_FAIL';

export const BANK_RESTRICTION_MAP = {
  1: 'PENDING_TRANSACTION',
  2: 'AUTOMATED_TRANSACTION',
  3: 'BLOCKING_SERVICE_FEATURE',
  4: 'SERVICE_FEATURE_LOCK',
  5: 'CALL_REQUIRED'
};

export const tokenDisclaimer = "Note: For linked banks through Plaid; PNC, Chase and some others will use tokenized account and/or routing numbers (not your actual account or routing number) to protect your bank information, so you may see an account number that is not yours. Please reach out to your bank with any questions regarding tokenized account or routing numbers.";

/* don't worry about html in the string, showdown will convert via content converter so html, not jsx is okay */
/* eslint-disable no-unused-vars */
const { automaticTransactionsLink } = preferencesLinks;
export const BANK_RESTRICTIONS = {
  [BANK_RESTRICTION_MAP[1]]:
    'There is a pending transaction associated with this bank. Therefore, the bank cannot be deleted and update options may be limited.',
  [BANK_RESTRICTION_MAP[2]]:
  '<span>There is an <a href="' +
  automaticTransactionsLink +
  '">automated transaction</a> associated with this bank. Therefore, the bank cannot be deleted and update options may be limited.</span>',
  [BANK_RESTRICTION_MAP[3]]:
    'A blocking service feature is associated with this bank.',
  [BANK_RESTRICTION_MAP[4]]: 'Service Feature Lock',
  [BANK_RESTRICTION_MAP[5]]: 'Call Required'
};

export const isQualifiedAccount = (accountTypeId) => {
  const qualifiedAccountTypes = [
    INDIVIDUAL,
    JOINT_TENANT,
    COLLEGE_SAVINGS,
    ROTH_ROLLOVER_IRA,
    TRADITIONAL_IRA,
    ROTH_IRA,
    ROLLOVER_IRA,
    UGMA_UTMA,
    TRUST,
    TRANSFER_ON_DEATH
  ];
  return qualifiedAccountTypes.some(
    (qualifiedAccountType) => qualifiedAccountType === accountTypeId
  );
};

const formatUpdateRequest = (
  associations,
  associationsToUpdate,
  bank,
  accountsById,
  isDeleteCompleteBank = false
) => {
  const { bankAgreementKey } = bank;

  const associatedAccounts = associationsToUpdate.map((association) => {
    const account = accountsById[association];
    if (account) {
      return getAccountDescriptor(account);
    }
  });

  if(newDeleteBankEnabled()) {
    return {
      bankAccount: {
        bankAgreementKey
      },
      associatedAccounts,
      isDeleteCompleteBank
    };
  } else {
    return {
      bankAccount: {
        bankAgreementKey
      },
      associatedAccounts
    };
  }
};

export const updateExistingBankAssociations = (
  bankAccountToUpdate,
  updatedAssociations,
  isAdd,
  callback
) => {
  const { bankAssociations } = bankAccountToUpdate;
  const accountsById = getAccountsById(Store.getState());

  if (bankAssociations) {
    const updateAssociationsRequest = formatUpdateRequest(
      bankAssociations,
      updatedAssociations,
      bankAccountToUpdate,
      accountsById
    );
    if (isAdd) {
      // UPDATE - ADDITIONS ONLY
      updateBankAssociations(updateAssociationsRequest, callback);
    } else {
      // DELETE - used when removing an existing association
      deleteBank(updateAssociationsRequest, callback);
    }
  }
};

export const deleteExistingBank = (bankAccountToUpdate, callback, isDeleteCompleteBank = false) => {
  const accountsById = getAccountsById(Store.getState());
  const { bankAssociations } = bankAccountToUpdate;
  if (bankAssociations) {
    const bankAssociationsToDelete = bankAssociations.map(
      (assoc) =>
        assoc && assoc.accountDescriptor && assoc.accountDescriptor.accountId
    );
    const deleteAssociationsRequest = formatUpdateRequest(
      bankAssociations,
      bankAssociationsToDelete,
      bankAccountToUpdate,
      accountsById,
      isDeleteCompleteBank
    );
    deleteBank(deleteAssociationsRequest, callback, true);
  }
};

export const challengeDeposit = (bankAccount, deposits, callback) => {
  const cleanedDeposits = deposits.map((deposit) => {
    return deposit.replace('$', '');
  });
  const depositVerificationOne = parseFloat(cleanedDeposits[0]);
  const depositVerificationTwo = parseFloat(cleanedDeposits[1]);

  const payload = {
    bankAccount: prepareBankPayload(bankAccount),
    bankChallengeDepositData: {
      depositVerificationOne,
      depositVerificationTwo
    }
  };

  challengeDepositVerification(payload, callback);
};

export const formatBankAccount = (bankAccount) => {
  const {bankAssociations} = bankAccount;
  if(bankAssociations) {
    const updatedAssociations = bankAssociations.map((association) => {
      if (association) {
        const newAssociation = omit(association, [
          'hiddenAccount'
          ]
        );
        return {
          ...newAssociation
        };
      }
    });

    return {
      ...bankAccount,
      bankAssociations: updatedAssociations
    };
  }
}

export const prepareBankPayload = (bankAccount) => {
  const accountsById = getAccountsById(Store.getState());
  const { bankAssociations } = bankAccount;

  if (bankAssociations) {
    const updatedAssociations = bankAssociations.map((association) => {
      const {
        accountDescriptor: { accountId }
      } = association;
      const account = accountsById[accountId];
      if (account) {
        const newAssociation = omit(association, [
          'hiddenAccount'
          ]
        );
        const accountDescriptor = getAccountDescriptor(account);
        return {
          ...newAssociation,
          accountDescriptor: accountDescriptor
        };
      }
    });

    const transformedBank = omit(bankAccount, [
      'accounts',
      'bankRestrictions',
      'verification',
      'eligibleToUpdate',
      'pendingUpdate',
      'allAccountsHidden',
      'justAdded',
      'allAccountsPending'
    ]);

    return {
      ...transformedBank,
      bankAssociations: updatedAssociations
    };
  }
};

export const BANK_EVENT_TYPES = {
  COMPLETE: 'COMPLETE_BANK_AGREEMENT_VERIFICATION',
  UPDATE: 'UPDATE_BANK_AGREEMENT',
  DELETE: 'DELETE_BANK_AGREEMENT'
};

export const buildBusinessEvent = (
  eventType,
  email,
  customer,
  view,
  bankAccount,
  associationIds = []
) => {
  const accountsById = getAccountsById(Store.getState());
  const { bankAssociations } = bankAccount;

  const { hasMaryland529Only, hasAlaska529Only, hasNational529Only } = view;

  let accountDescriptors = [];

  if (associationIds) {
    associationIds.forEach((id) => {
      const account = accountsById[id];
      if (account) {
        const accountDescriptor = getAccountDescriptor(account);
        accountDescriptors.push({
          ...accountDescriptor,
          accountName: account.accountName
        });
      }
    });
  }

  if (bankAssociations) {
    const transformedAssociations = bankAssociations.map((association) => {
      const {
        accountDescriptor: { accountId }
      } = association;
      const account = accountsById[accountId];
      if (account) {
        const newAssociation = omit(association, [
          'hiddenAccount'
          ]
        );
        const accountDescriptor = getAccountDescriptor(account);
        return {
          ...newAssociation,
          accountDescriptor: accountDescriptor
        };
      }
    });

    const clientSegment = hasMaryland529Only
      ? 'MD529'
      : hasAlaska529Only
        ? 'UA529'
        : hasNational529Only
          ? 'TRP529'
          : 'RETAIL';
    const transformedBank = omit(bankAccount, [
      'accounts',
      'verification',
      'eligibleToUpdate',
      'pendingUpdate',
      'allAccountsHidden',
      'justAdded',
      'allAccountsPending',
    ]);

    return {
      eventType: eventType,
      emailAddress: email,
      brandingProfile: clientSegment,
      entityKey: {
        businessPartyId: customer.businessPartyId,
        uberId: customer.uberId
      },
      bankAccount: {
        ...transformedBank,
        bankAssociations: transformedAssociations
      },
      accounts: accountDescriptors
    };
  }
};

export const REINITIATE_EVENT_TYPES = {
  REINITIATE: 'START_BANK_AGREEMENT_VERIFICATION'
};

export const buildReinitiateChallengeDepositsBusinessEvent = (
  eventType,
  email,
  customer,
  view,
  bankAccount = []
) => {
  const accountsById = getAccountsById(Store.getState());
  const { bankAssociations } = bankAccount;

  const { hasMaryland529Only, hasAlaska529Only, hasNational529Only } = view;

  if (bankAssociations) {
    const transformedAssociations = bankAssociations.map((association) => {
      const {
        accountDescriptor: { accountId }
      } = association;
      const account = accountsById[accountId];
      if (account) {
        const newAssociations = omit(association, [
          'hiddenAccount'
          ]
        );
        const accountDescriptor = getAccountDescriptor(account);
        return {
          ...newAssociations,
          accountDescriptor: accountDescriptor
        };
      }
    });

    const clientSegment = hasMaryland529Only
      ? 'MD529'
      : hasAlaska529Only
        ? 'UA529'
        : hasNational529Only
          ? 'TRP529'
          : 'RETAIL';
    const transformedBank = omit(bankAccount, [
      'accounts',
      'verification',
      'eligibleToUpdate',
      'pendingUpdate',
      'allAccountsHidden',
      'justAdded',
      'allAccountsPending'
    ]);

    return {
      eventType: eventType,
      emailAddress: email,
      brandingProfile: clientSegment,
      entityKey: {
        businessPartyId: customer.businessPartyId,
        uberId: customer.uberId
      },
      bankAccount: {
        ...transformedBank,
        bankAssociations: transformedAssociations
      }
    };
  }
};

export const areBanksWithoutIdEqual = (b1, b2) => {
  if (!b1 || !b2) {
    return false;
  }

  return Boolean(
    b1.routingNumber &&
    b2.routingNumber &&
    b1.routingNumber === b2.routingNumber &&
    ((b1.accountTypeId &&
        b2.accountTypeId &&
        b1.accountTypeId === b2.accountTypeId) ||
      (b1.accountTypeName &&
        b2.accountTypeName &&
        b1.accountTypeName === b2.accountTypeName)) &&
    b1.lastFour &&
    b2.lastFour &&
    b1.lastFour === b2.lastFour
  );
};

export const getAllAssociationsFromBank = (bank) => {
  if (!bank) {
    return [];
  }
  if (bank.bankAssociations && bank.bankAgreementKey) {
    const { bankAssociations } = bank;
    return bankAssociations.filter((ba) =>
      areBanksWithoutIdEqual(ba.bankAgreementKey, bank.bankAgreementKey)
    );
  }
  return [];
};

export const getAssociatedBanksFromFund = (banks, fundToMatch) => {
  if (!banks || !fundToMatch) {
    return [];
  }
  const matchingBanks = banks.filter((b) => {
    if (b && b.bankAssociations && fundToMatch && fundToMatch.accountId) {
      return b.bankAssociations.some(
        (ba) => ba.accountDescriptor.accountId === fundToMatch.accountId
      );
    }
  });

  const associatedReplacements = matchingBanks.map((bank) => {
    const replacementBankAssociation = bank.bankAssociations.find(
      (ba) => ba.accountDescriptor.accountId === fundToMatch.accountId
    );
    // this needs to be done for certain associations with different bank ids that need to remain
    // example: div cap gains
    const replacementBank = {
      ...bank
    };
    replacementBank.bankAgreementKey.bankId =
      replacementBankAssociation.bankAgreementKey.bankId;
    return replacementBankAssociation ? replacementBank : bank;
  });

  return associatedReplacements;
};

export const areBanksEqual = (b1, b2) => {
  if (!b1 || !b2) {
    return false;
  }

  return Boolean(
    b1.bankId &&
    b2.bankId &&
    b1.bankId == b2.bankId &&
    b1.routingNumber &&
    b2.routingNumber &&
    b1.routingNumber == b2.routingNumber &&
    ((b1.accountTypeId &&
        b2.accountTypeId &&
        b1.accountTypeId == b2.accountTypeId) ||
      (b1.accountTypeName &&
        b2.accountTypeName &&
        b1.accountTypeName == b2.accountTypeName)) &&
    b1.lastFour &&
    b2.lastFour &&
    b1.lastFour == b2.lastFour
  );
};
